// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";

// ** API Imports
import { saveJobTitle } from "../logic/JobTitlelogic";

const JobTitleDialog = ({ open, handleClose, JobTitleProps }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    existingJobTitle,
    fetchJobTitles,
    jobTitleContent,
    onJobTitleCreated,
  } = JobTitleProps;

  const [jobTitle, setJobTitle] = useState({
    jobtitleID: 0,
    titleName: "",
    description: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({
    titleName: false,
    titleNameMsg: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (jobTitleContent.entity === 'create') {
      resetJobTitle();
    }

    if (jobTitleContent.entity === 'edit' && existingJobTitle) {
      setJobTitle({
        jobtitleID: existingJobTitle.jobtitleID || 0,
        titleName: existingJobTitle.titleName || "",
        description: existingJobTitle.description || "",
        isActive: existingJobTitle.isActive !== undefined ? existingJobTitle.isActive : true,
      });
    }
  }, [open, existingJobTitle]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobTitle((prevJobTitle) => ({
      ...prevJobTitle,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateJobTitle = (titleName) => {
    if (!titleName?.trim()) {
      return { error: true, message: 'Title Name is required' };
    }

    if (titleName.trim().length < 2) {
      return { error: true, message: 'Title Name must be at least 2 characters long' };
    }

    return { error: false, message: '' };
  };

  const handleSave = async () => {
    const titleNameError = validateJobTitle(jobTitle.titleName);

    const newErrors = {
      titleName: titleNameError.error,
      titleNameMsg: titleNameError.message,
    };

    setErrors(newErrors);

    if (titleNameError.error) {
      toast.error("Please fill out all required fields correctly", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      return;
    }

    setLoading(true);

    try {
      const result = await saveJobTitle(jobTitle, navigate);
      
      if (result && !result.isError) {
        const isEdit = jobTitleContent?.entity === 'edit';
        const message = isEdit 
          ? "Job Title Updated Successfully" 
          : "Job Title Added Successfully";
        
        toast.success(message, {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
        
        if (fetchJobTitles) {
          await fetchJobTitles();
        }
        
        if (onJobTitleCreated) {
          onJobTitleCreated(result);
        }
        
        resetJobTitle();
        handleClose();
      } else {
        toast.error(result?.message || "Failed to save job title");
      }
    } catch (error) {
      console.error("Error saving job title:", error);
      toast.error("An error occurred while saving the job title", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const resetJobTitle = () => {
    setJobTitle({
      jobtitleID: 0,
      titleName: "",
      description: "",
      isActive: true,
    });
    setErrors({
      titleName: false,
      titleNameMsg: '',
    });
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {jobTitleContent?.title || "Add Job Title"}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 1, mb: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                required
                label="Title Name"
                name="titleName"
                value={jobTitle.titleName}
                placeholder="Enter title name"
                onChange={handleChange}
                error={errors.titleName}
                helperText={errors.titleName ? errors.titleNameMsg : ''}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={jobTitle.description}
                placeholder="Enter description (optional)"
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button 
              variant="contained" 
              color="info" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              color="main" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : (jobTitleContent?.entity === 'edit' ? 'Save Changes' : 'Save Job Title')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JobTitleDialog;